import { useLingui as _useLingui } from "../../lib/i18n/useLingui";
import { Chip } from "../../components/Chip";
import { formatNumber, useFormatLocale } from "../../lib/i18n/format";
import { cn } from "../../lib/utils";
import React, { useState } from "react";

// Constants for consistent styling
const VALUE_CLASSES = "text-primary-700 pt-1 text-sm";
const EMPTY_CLASSES = "text-primary-500 pt-1 text-sm italic";
const INDENT_CLASSES = "border-structure-200 max-w-full border-l pl-4 ml-4";

// Performance limits to prevent browser crashes.
// These limits are meant to be very conservative.
const MAX_OBJECT_DEPTH = 8;
const MAX_ARRAY_ITEMS = 128;
const MAX_OBJECT_KEYS = 64;
const MAX_STRING_LENGTH = 1024;
// Helper component for inline expand/collapse buttons with consistent styling.
function InlineExpandButton({
  label,
  buttonText,
  onClick,
  className
}) {
  return /*#__PURE__*/React.createElement("span", {
    className: cn(EMPTY_CLASSES, className)
  }, label, " ", /*#__PURE__*/React.createElement("button", {
    onClick: onClick,
    className: "cursor-pointer font-medium text-highlight hover:underline"
  }, buttonText));
}

// Helper component for rendering key-value pairs with consistent styling.
function KeyValuePair({
  keyName,
  value,
  depth,
  chipColor,
  isRootLevel = false,
  expandedPaths,
  setExpandedPaths,
  currentPath
}) {
  const isComplexValue = typeof value === "object" && value !== null;
  if (isComplexValue) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Chip, {
      size: "xs",
      color: chipColor,
      label: formatKey(keyName),
      className: "mb-2"
    }), /*#__PURE__*/React.createElement("div", {
      className: cn("max-w-full", isRootLevel && "ml-4")
    }, /*#__PURE__*/React.createElement(JsonValue, {
      value: value,
      depth: depth + 1,
      expandedPaths: expandedPaths,
      setExpandedPaths: setExpandedPaths,
      currentPath: `${currentPath}.${keyName}`
    })));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: cn("flex items-start", isRootLevel ? "gap-3" : "gap-2")
  }, /*#__PURE__*/React.createElement(Chip, {
    size: "xs",
    color: chipColor,
    label: formatKey(keyName)
  }), /*#__PURE__*/React.createElement(JsonValue, {
    value: value,
    depth: depth + 1,
    expandedPaths: expandedPaths,
    setExpandedPaths: setExpandedPaths,
    currentPath: `${currentPath}.${keyName}`
  }));
}
function JsonValue({
  value,
  depth = 0,
  expandedPaths = new Set(),
  setExpandedPaths,
  currentPath = ""
}) {
  const {
    _: _t
  } = _useLingui();
  const formatLocale = useFormatLocale();
  const expandLabel = _t(
  /** i18n */
  {
    id: "x-kLuk",
    message: "expand",
    context: "verb, show hidden JSON content"
  });
  const handleToggleExpanded = path => {
    if (!setExpandedPaths) {
      return;
    }
    setExpandedPaths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };
  if (depth >= MAX_OBJECT_DEPTH && typeof value === "object" && value !== null) {
    var _expandedPaths$has;
    const deepObjectPath = `${currentPath}:deep`;
    const isExpanded = (_expandedPaths$has = expandedPaths === null || expandedPaths === void 0 ? void 0 : expandedPaths.has(deepObjectPath)) !== null && _expandedPaths$has !== void 0 ? _expandedPaths$has : false;
    if (isExpanded) {
      // Render the full object/array when expanded, ignoring depth limit.
      return /*#__PURE__*/React.createElement(JsonValue, {
        value: value,
        depth: 0 // Reset depth to allow full rendering.
        ,
        expandedPaths: expandedPaths,
        setExpandedPaths: setExpandedPaths,
        currentPath: `${currentPath}:expanded`
      });
    }
    return /*#__PURE__*/React.createElement("div", {
      className: "flex items-center gap-1"
    }, /*#__PURE__*/React.createElement(InlineExpandButton, {
      label: _t(
      /** i18n */
      {
        id: "mqvaYw",
        message: "Maximum depth reached"
      }),
      buttonText: expandLabel,
      onClick: () => handleToggleExpanded(deepObjectPath)
    }));
  }
  if (value === null || value === undefined) {
    return /*#__PURE__*/React.createElement("span", {
      className: EMPTY_CLASSES
    }, _t(
    /** i18n */
    {
      id: "Hmm14Y",
      message: "empty",
      context: "a null JSON value"
    }));
  }
  if (typeof value === "boolean") {
    return /*#__PURE__*/React.createElement("span", {
      className: VALUE_CLASSES
    }, value ? _t(
    /** i18n */
    {
      id: "l75CjT",
      message: "Yes"
    }) : _t(
    /** i18n */
    {
      id: "1UzENP",
      message: "No"
    }));
  }
  if (typeof value === "number") {
    return /*#__PURE__*/React.createElement("span", {
      className: VALUE_CLASSES
    }, value);
  }
  if (typeof value === "string") {
    if (value.length > MAX_STRING_LENGTH) {
      var _expandedPaths$has2;
      const longStringPath = `${currentPath}:longstring`;
      const isExpanded = (_expandedPaths$has2 = expandedPaths === null || expandedPaths === void 0 ? void 0 : expandedPaths.has(longStringPath)) !== null && _expandedPaths$has2 !== void 0 ? _expandedPaths$has2 : false;
      const hiddenCharCount = value.length - MAX_STRING_LENGTH;
      // Plural forms are picked with the count, but `#` would format it in the catalog's locale.
      const hiddenChars = formatNumber(hiddenCharCount, undefined, formatLocale);
      return /*#__PURE__*/React.createElement("span", {
        className: cn(VALUE_CLASSES, "whitespace-pre-wrap break-normal")
      }, isExpanded ? value : value.substring(0, MAX_STRING_LENGTH), !isExpanded && "…", " ", /*#__PURE__*/React.createElement("button", {
        onClick: () => handleToggleExpanded(longStringPath),
        className: "cursor-pointer font-medium text-highlight hover:underline"
      }, isExpanded ? _t(
      /** i18n */
      {
        id: "6Oro3P",
        message: "collapse",
        context: "verb, hide JSON content"
      }) : _t(
      /** i18n */
      {
        id: "20nSK3",
        message: "expand ({hiddenCharCount, plural, one {{hiddenChars} more character} other {{hiddenChars} more characters}})",
        values: {
          hiddenCharCount: hiddenCharCount,
          hiddenChars: hiddenChars
        }
      })));
    }
    return /*#__PURE__*/React.createElement("span", {
      className: cn(VALUE_CLASSES, "whitespace-pre-wrap break-normal")
    }, value);
  }
  if (Array.isArray(value)) {
    var _expandedPaths$has3;
    if (value.length === 0) {
      return /*#__PURE__*/React.createElement("span", {
        className: EMPTY_CLASSES
      }, _t(
      /** i18n */
      {
        id: "-I6Rv8",
        message: "empty list"
      }));
    }

    // Check if it's a simple array of primitives.
    const isSimpleArray = value.every(item => typeof item !== "object" || item === null);
    if (isSimpleArray && value.length <= 5) {
      return /*#__PURE__*/React.createElement("span", {
        className: VALUE_CLASSES
      }, value.map((item, index) => /*#__PURE__*/React.createElement("span", {
        key: index
      }, /*#__PURE__*/React.createElement(JsonValue, {
        value: item,
        depth: depth + 1,
        expandedPaths: expandedPaths,
        setExpandedPaths: setExpandedPaths,
        currentPath: `${currentPath}[${index}]`
      }), index < value.length - 1 && ", ")));
    }

    // Truncate arrays that have too many items.
    const arrayPath = `${currentPath}[]`;
    const isExpanded = (_expandedPaths$has3 = expandedPaths === null || expandedPaths === void 0 ? void 0 : expandedPaths.has(arrayPath)) !== null && _expandedPaths$has3 !== void 0 ? _expandedPaths$has3 : false;
    const itemsToShow = isExpanded ? value.length : Math.min(value.length, MAX_ARRAY_ITEMS);
    const hasMore = value.length > MAX_ARRAY_ITEMS && !isExpanded;
    const hiddenItemCount = value.length - itemsToShow;
    const hiddenItems = formatNumber(hiddenItemCount, undefined, formatLocale);
    return /*#__PURE__*/React.createElement("div", {
      className: "mt-2"
    }, value.slice(0, itemsToShow).map((item, index) => {
      const itemNumber = formatNumber(index + 1, undefined, formatLocale);
      return /*#__PURE__*/React.createElement("div", {
        key: index,
        className: cn(INDENT_CLASSES)
      }, /*#__PURE__*/React.createElement("div", {
        className: "flex flex-col gap-2"
      }, /*#__PURE__*/React.createElement(Chip, {
        size: "xs",
        color: "primary",
        label: _t(
        /** i18n */
        {
          id: "cMNvzX",
          message: "Item {itemNumber}",
          values: {
            itemNumber: itemNumber
          }
        })
      }), /*#__PURE__*/React.createElement("div", {
        className: "max-w-full"
      }, /*#__PURE__*/React.createElement(JsonValue, {
        value: item,
        depth: depth + 1,
        expandedPaths: expandedPaths,
        setExpandedPaths: setExpandedPaths,
        currentPath: `${currentPath}[${index}]`
      }))));
    }), hasMore && /*#__PURE__*/React.createElement("div", {
      className: cn(INDENT_CLASSES)
    }, /*#__PURE__*/React.createElement(InlineExpandButton, {
      label: _t(
      /** i18n */
      {
        id: "fqIn0u",
        message: "{hiddenItemCount, plural, one {{hiddenItems} more item} other {{hiddenItems} more items}}",
        values: {
          hiddenItemCount: hiddenItemCount,
          hiddenItems: hiddenItems
        }
      }),
      buttonText: expandLabel,
      onClick: () => handleToggleExpanded(arrayPath)
    })));
  }
  if (typeof value === "object") {
    var _expandedPaths$has4;
    const entries = Object.entries(value);
    if (entries.length === 0) {
      return /*#__PURE__*/React.createElement("span", {
        className: EMPTY_CLASSES
      }, _t(
      /** i18n */
      {
        id: "2-lrra",
        message: "empty",
        context: "an empty JSON object"
      }));
    }

    // Truncate objects with too many properties.
    const objectPath = `${currentPath}{}`;
    const isExpanded = (_expandedPaths$has4 = expandedPaths === null || expandedPaths === void 0 ? void 0 : expandedPaths.has(objectPath)) !== null && _expandedPaths$has4 !== void 0 ? _expandedPaths$has4 : false;
    const keysToShow = isExpanded ? entries.length : Math.min(entries.length, MAX_OBJECT_KEYS);
    const hasMore = entries.length > MAX_OBJECT_KEYS && !isExpanded;
    const visibleEntries = entries.slice(0, keysToShow);
    const hiddenPropertyCount = entries.length - keysToShow;
    const hiddenProperties = formatNumber(hiddenPropertyCount, undefined, formatLocale);
    const hiddenPropertiesLabel = _t(
    /** i18n */
    {
      id: "EodAlr",
      message: "{hiddenPropertyCount, plural, one {{hiddenProperties} more property} other {{hiddenProperties} more properties}}",
      values: {
        hiddenPropertyCount: hiddenPropertyCount,
        hiddenProperties: hiddenProperties
      }
    });

    // For nested objects, use a card-like layout with vertical bars.
    if (depth > 0) {
      return /*#__PURE__*/React.createElement("div", {
        className: "space-y-2"
      }, visibleEntries.map(([key, val]) => /*#__PURE__*/React.createElement("div", {
        key: key,
        className: cn(INDENT_CLASSES)
      }, /*#__PURE__*/React.createElement(KeyValuePair, {
        keyName: key,
        value: val,
        depth: depth,
        chipColor: "info",
        expandedPaths: expandedPaths,
        setExpandedPaths: setExpandedPaths,
        currentPath: currentPath
      }))), hasMore && /*#__PURE__*/React.createElement("div", {
        className: cn(INDENT_CLASSES)
      }, /*#__PURE__*/React.createElement(InlineExpandButton, {
        label: hiddenPropertiesLabel,
        buttonText: expandLabel,
        onClick: () => handleToggleExpanded(objectPath)
      })));
    }

    // Root level objects use a table-like layout.
    return /*#__PURE__*/React.createElement("div", {
      className: "max-w-full space-y-3"
    }, visibleEntries.map(([key, val]) => /*#__PURE__*/React.createElement("div", {
      key: key,
      className: cn("max-w-full border-b pb-3 last:border-0 last:pb-0", "border-structure-200")
    }, /*#__PURE__*/React.createElement(KeyValuePair, {
      keyName: key,
      value: val,
      depth: depth,
      chipColor: "highlight",
      isRootLevel: true,
      expandedPaths: expandedPaths,
      setExpandedPaths: setExpandedPaths,
      currentPath: currentPath
    }))), hasMore && /*#__PURE__*/React.createElement("div", {
      className: "border-structure-200 border-t pt-3"
    }, /*#__PURE__*/React.createElement(InlineExpandButton, {
      label: hiddenPropertiesLabel,
      buttonText: expandLabel,
      onClick: () => handleToggleExpanded(objectPath)
    })));
  }
  return /*#__PURE__*/React.createElement("span", {
    className: cn("text-sm", "text-element-700")
  }, String(value));
}
function formatKey(key) {
  // Convert snake_case or camelCase to Title Case.
  return key.replace(/_/g, " ").replace(/([A-Z])/g, " $1").trim().split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ");
}
/**
 * Human-friendly viewer for parsed JSON, rendering keys as chips and nesting
 * with indent guides, with expand/collapse for deep, long, or large values.
 * Used by CodeBlockWithExtendedSupport as the "Pretty JSON" view of fenced
 * JSON blocks in Markdown output.
 * @summary Pretty-printed JSON viewer.
 */
export function PrettyJsonViewer({
  data,
  className
}) {
  const [expandedPaths, setExpandedPaths] = useState(new Set());
  return /*#__PURE__*/React.createElement("div", {
    className: cn("max-w-full min-w-0 overflow-x-auto overflow-y-visible", "bg-structure-50", "rounded-lg px-4 py-4 text-base", className)
  }, /*#__PURE__*/React.createElement("div", {
    className: "max-w-full overflow-x-auto"
  }, /*#__PURE__*/React.createElement(JsonValue, {
    value: data,
    expandedPaths: expandedPaths,
    setExpandedPaths: setExpandedPaths,
    currentPath: "root"
  })));
}
//# sourceMappingURL=PrettyJsonViewer.js.map